import { explainerFromLadder } from './report-blocks/explainer-from-ladder';
import { blockVariableKeys, layoutRows } from './report-aggregation';
import { EXAMPLE_PART_POINTS } from './report-blocks/block-examples';
import type { StageMatrixExtraData } from '../../models/end-client-reporting-component-types/stage-matrix';
import type { StageExplainerExtraData } from '../../models/end-client-reporting-component-types/stage-explainer';
import {
  ColorPalette,
  PageSection,
  ReportDesign,
  ReportLogo,
  ReportSubSection,
  ReportSection,
  ReportStatistics,
  ReportType,
  SegmentLocation,
  VariableSlot,
} from '../../models/end-client-reporting-package';
import { exampleExplainer, exampleLadder } from './report-blocks/block-examples';
import type { ChartOrientation } from './report-blocks/chart-layout';
import type { SurveyLayout } from './report-aggregation';

/**
 * Pure `(design) => design` helpers behind the deck's inline editing. Every one returns a new
 * document; the deck holds the result in a signal and PUTs it on "Done editing".
 *
 * These are deliberately per-field. The whole-subsection API they replaced rebuilt a block's
 * `reportExtraData` from scratch out of an intermediate form, which twice destroyed settings that
 * form did not happen to carry. Patching one block cannot lose a field it never looked at.
 */

/** A charted variable, with its data position when it is not yet in the design's `variableMap`. */
export interface PartVariable {
  key: string;
  position?: string;
}

/** The editable fields of one chart block, all optional so a caller patches only what changed. */
export interface BlockPatch {
  header?: string;
  segmentLocation?: SegmentLocation;
  orientation?: ChartOrientation;
  columns?: number;
  includeTotal?: boolean;
  sortDescending?: boolean;
  topN?: number;
  groupBy?: string;
  // Whether a numeric battery's figures read as scores or percentages, and whether the labels are
  // drawn on the chart at all. Both are per-block appearance rather than arithmetic: the same
  // question charted twice in one deck may want them differently. `fieldAppliesTo` decides which
  // modules offer them -- a word cloud has nothing to label, and only a bar chart has a unit.
  valueUnit?: 'score' | 'percent';
  hideLabels?: boolean;
  // Bar chart only: answers nobody picked are drawn at 0% unless this is set.
  hideEmptyAnswers?: boolean;
  // Methodology only. Blank means "use the computed figure" for the two that have one.
  sampleSize?: string;
  medianLoi?: string;
  incidenceRate?: string;
  market?: string;
  notesHeading?: string;
  notes?: string;
  // Executive summary only. Always patched whole -- the boxes are positional, so a partial array
  // would silently renumber every box after the one that changed.
  boxes?: { value?: string; label?: string }[];
  takeaways?: string[];
  // Stacked distribution only. `netExclude` is set by the generated design rather than the editor:
  // it names answers that are not on the scale, which the agent reads from the answer text.
  showNet?: boolean;
  netLabel?: string;
  netBoxes?: number;
  netThreshold?: number;
  netTopBox?: 'first' | 'last';
  barOrder?: 'asked' | 'net-high' | 'net-low' | 'share-high' | 'share-low';
  // One band's share: headlined above a single bar, or a column down a battery in the net's place.
  showShare?: boolean;
  shareCaption?: string;
  // Funnel only.
  showDropOff?: boolean;
  // Modules that reduce a question to one figure. Patched whole -- it is a set, and a partial one
  // would mean a different sum.
  countedAnswers?: string[];
  // Named panels or tiles, which may come from more than one question. Patched whole: they are
  // positional, so a partial list would reorder everything after the entry that changed.
  subgroups?: { variableKey: string; answer: string }[];
  // Recommended actions only. Patched whole for the same reason the boxes are: the entries are
  // positional, and a partial array would renumber every action after the one that changed.
  actions?: { text?: string; priority?: 'high' | 'medium' | 'low' }[];
  // Head-to-head only. Which pole is drawn on the left, what the two sides and the margin column
  // are called, and whether that column is drawn. The three labels are free text: what a study
  // calls its sides is the study's own wording, and no phrasing baked in here would survive the
  // next survey.
  leftAnswer?: 'first' | 'last';
  leftLabel?: string;
  rightLabel?: string;
  gapLabel?: string;
  showGap?: boolean;
  // Ranked driver only. How many rank positions count as "top", which end of the answer list is
  // rank #1, and how the sorted rows are banded. `tierLabels` is patched whole: the names are
  // positional, so a partial list would rename every tier after the one that changed.
  topRanks?: number;
  rankOneEnd?: 'first' | 'last';
  tierCount?: number;
  tierLabels?: string[];
  // Named themes over a battery's rows, shared with the ranked bar. Patched whole: a category owns
  // a list, and a partial one would silently drop rows out of their theme.
  categories?: { label: string; items: string[] }[];
  // Verbatim explorer only. How many answers show before "Show more", and whether the search box is
  // drawn at all.
  pageSize?: number;
  searchable?: boolean;
  // Word cloud only. `stopwords` is patched whole -- it is a set, and a partial one means a
  // different cloud.
  maxWords?: number;
  minCount?: number;
  stopwords?: string[];
  useQuestionWords?: boolean;
  // Heatmap: `imageId` names an uploaded image in place of the survey's; blank goes back to it.
  // Image module: it names the picture itself.
  imageId?: string;
  showOptOut?: boolean;
  sharedScale?: boolean;
  // Text module: its content, as the editor writes it. Image module: the picture's description.
  html?: string;
  altText?: string;
  // Positioning scatter only. The axis titles and the caption are free text: an axis title is a
  // phrase where the question it comes from is a sentence.
  xLabel?: string;
  yLabel?: string;
  showQuadrants?: boolean;
  quadrantLabel?: string;
  // Ownership matrix only. `priorityKeys` names a whole ranking grid, so it is patched with its
  // questions rather than as a plain field -- see setBlockPriority.
  priorityLabel?: string;
  showWhiteSpace?: boolean;
  whiteSpaceLabel?: string;
  rowOrder?: 'asked' | 'priority';
  // Relationship ladder only. All three are authored: nothing in a datamap says which rungs group
  // together, what to call the group, or what movement across them means. Patched whole, being
  // ordered lists where a partial one would regroup everything after the entry that changed.
  stages?: { label: string; rungs: number }[];
  totalLabel?: string;
  totalExcludes?: string[];
  spectrum?: string[];
  // What the columns are called: the deck's wording for the rungs, not the survey's. The answer
  // text stays the rung's identity -- `totalExcludes` names answers -- so renaming a column here
  // changes nothing but the header.
  rungNames?: string[];
  // Explainer panel only. Every word of it is written, so the whole thing is one patched field.
  panels?: { title: string; parts?: { heading: string; points?: string[] }[] }[];
  // Data table only. What a cell reads as, and whether the count is printed under it.
  cellValue?: 'percent' | 'count';
  showCounts?: boolean;
  // Crosstab only. Which significance test marks the cells, the summary rows, and how cells are
  // shaded. `nets` and `meanExcludes` are patched whole: a net is a set of answers, and a partial
  // one would be a different net. Its own filter goes through setBlockTableFilter, which has to map
  // the questions it names.
  sigTest?: 'none' | 'columns' | 'total';
  nets?: { label: string; answers: string[] }[];
  showMean?: boolean;
  meanExcludes?: string[];
  shading?: 'none' | 'heat' | 'significance';
  layerView?: 'nested' | 'tabs' | 'grid';
  // Single-KPI dial only. `label` is its subheading, `value` the typed figure -- which stays a
  // typed figure even where the dial computes one, since it doubles as the override.
  value?: string;
  label?: string;
  scaleMax?: number;
  scaleMin?: number;
  metric?: 'net' | 'share';
  shareBand?: number;
  display?: 'dial' | 'figure';
  // The one line around the figure that is a judgement rather than a reading. Its heading and its
  // verdict are built from `netLabel` and the sign of the score.
  caption?: string;
  // Named groups over an ordered answer list, shared by the dial and the bar chart. Patched whole:
  // they are counted off one end, so a partial list would regroup every answer after the entry that
  // changed. Restated here rather than imported -- report-design-edit -> chart-layout ->
  // report-aggregation is a runtime cycle, which is why `stages` above is restated too.
  bands?: { label: string; options: number }[];
  // Which end of the answer list the favourable answers sit at. Separate from `netTopBox`, which
  // governs the stacked distribution's symmetric box count: one field reinterpreting the other is
  // the inversion trap twice over.
  topBand?: 'first' | 'last';
  // Answers that are not on the scale at all, removed before the bands are walked. Shared with the
  // stacked distribution, where the agent rather than the editor sets it.
  netExclude?: string[];
}

/**
 * Describes every variable a design references, so the same design can later be re-bound to another
 * survey's questions — what turns a design into a template.
 *
 * Derived rather than authored: the wording of the question a reference was bound to is a better
 * description of what it's looking for than anything someone would type from scratch, and it comes
 * free. Built here, in the browser, because this is where the layout is: a template's content can
 * arrive from the template editor (bound to the sample survey) or from a real deck (bound to that
 * project's), and the server has no way to tell which.
 *
 * A key with no resolvable variable is skipped rather than given an empty description — a slot the
 * matcher can't act on is worse than no slot, since it would report as unmatched on every study.
 */
export function deriveVariableSlots(
  design: ReportDesign,
  layout: SurveyLayout,
): Record<string, VariableSlot> {
  const slots: Record<string, VariableSlot> = {};

  for (const [key, position] of Object.entries(design.variableMap)) {
    const variable = layout[position];
    if (!variable?.label) continue;
    // Numeric batteries are left out of template slots: `shape` has only the two values the matcher
    // prompt and slotMatchSchema know, and a score is neither. Matching one would need a third
    // shape end to end -- a separate change, not a side effect of charting them.
    if (variable.kind === 'numeric' || variable.kind === 'text' || variable.kind === 'heatmap') continue;
    // Nor one column of a question: `shape` has only the two values the matcher knows, and a slot
    // naming one option of one question could never be matched against a different survey.
    if (variable.partOf) continue;

    const optionLabels = (variable.kind === 'multi'
      ? variable.columns.map(column => column.label)
      : variable.options.map(option => option.label)
    ).filter(Boolean);

    slots[key] = {
      description: variable.label,
      // Only the distinction the datamap actually supports. A scale reads as coded like any other
      // single-select, and guessing at one from option labels would be a worse signal than none.
      shape: variable.kind === 'multi' ? 'multi-punch' : 'single-select',
      optionCount: optionLabels.length,
      // The count is the full one above; this is a sample. What the question *asks* is often absent
      // for hidden and derived variables -- the ones a template most wants -- but what it offers as
      // answers is always there, and says more.
      optionLabels: optionLabels.slice(0, MAX_SLOT_OPTION_LABELS),
    };
  }

  return slots;
}

/** Enough to recognise a question by, short of reproducing a long grid in a prompt. */
const MAX_SLOT_OPTION_LABELS = 8;

/**
 * A deck's design as a template made from it. Everything recording how *this deck* came to be is
 * dropped, because none of it is true of the template: generationGuidance is one study's brief and
 * would be copied into every deck built from it; templateId/templateVersion point at the template the
 * deck came from, not the one being made; and unresolvedSlots is what this survey couldn't answer,
 * which says nothing about the next. Shared by the deck and the reports list, so a template is the
 * same whichever it was saved from.
 *
 * The weighting goes too, while the rest of the statistics stay: a weight column or a set of
 * population targets belongs to one study's sample, where a confidence level is a house standard.
 */
export function templateDocumentFrom(design: ReportDesign, layout: SurveyLayout): ReportDesign {
  const { generationGuidance, templateId, templateVersion, unresolvedSlots, statistics, ...rest } = design;
  const kept = statistics ? { ...statistics } : undefined;
  if (kept) delete kept.weighting;
  return {
    ...rest,
    ...(kept && Object.keys(kept).length ? { statistics: kept } : {}),
    variableSlots: deriveVariableSlots(design, layout),
  };
}

/**
 * Clears one entry from the deck's unresolved list. Removing the key entirely once the last one goes
 * rather than leaving an empty array: absent means "nothing outstanding", which is what an empty
 * array would mean anyway, and it keeps the document free of bookkeeping nobody needs.
 */
export function removeUnresolvedSlot(design: ReportDesign, slot: string): ReportDesign {
  const remaining = (design.unresolvedSlots ?? []).filter(entry => entry.slot !== slot);
  const { unresolvedSlots, ...rest } = design;
  return remaining.length ? { ...rest, unresolvedSlots: remaining } : rest;
}

interface SubSectionLocation {
  sectionId: string;
  index: number;
}

function locate(design: ReportDesign, subSectionId: string): SubSectionLocation | null {
  for (const sectionId of design.sectionOrder) {
    const index = design.sections[sectionId]?.pageOrder.indexOf(subSectionId) ?? -1;
    if (index !== -1) return { sectionId, index };
  }
  return null;
}

function withSection(design: ReportDesign, sectionId: string, section: ReportSection): ReportDesign {
  return { ...design, sections: { ...design.sections, [sectionId]: section } };
}

function pruneEmptySections(design: ReportDesign): ReportDesign {
  const keep = design.sectionOrder.filter(id => (design.sections[id]?.pageOrder.length ?? 0) > 0);
  if (keep.length === design.sectionOrder.length) return design;

  const sections: Record<string, ReportSection> = {};
  for (const id of keep) sections[id] = design.sections[id];
  return { ...design, sections, sectionOrder: keep };
}

function detachSubSection(design: ReportDesign, subSectionId: string): ReportDesign {
  const at = locate(design, subSectionId);
  if (!at) return design;

  const section = design.sections[at.sectionId];
  const pages = { ...section.pages };
  delete pages[subSectionId];

  return withSection(design, at.sectionId, {
    ...section,
    pages,
    pageOrder: section.pageOrder.filter(id => id !== subSectionId),
  });
}

/** Appends `subsection` to an existing section, or to a new one named `newSectionName`. */
function attachSubSection(
  design: ReportDesign,
  subSection: ReportSubSection,
  target: { sectionId?: string; newSectionName?: string },
  newId: () => string,
): ReportDesign {
  if (target.newSectionName) {
    const sectionId = newId();
    return {
      ...design,
      sections: {
        ...design.sections,
        [sectionId]: {
          id: sectionId,
          name: target.newSectionName,
          pages: { [subSection.id]: subSection },
          pageOrder: [subSection.id],
        },
      },
      sectionOrder: [...design.sectionOrder, sectionId],
    };
  }

  const sectionId = target.sectionId && design.sections[target.sectionId]
    ? target.sectionId
    : design.sectionOrder[design.sectionOrder.length - 1];
  const section = sectionId ? design.sections[sectionId] : undefined;

  // A design with no sections at all still has to be able to take its first subsection.
  if (!section) return attachSubSection(design, subSection, { newSectionName: 'Report' }, newId);

  return withSection(design, sectionId, {
    ...section,
    pages: { ...section.pages, [subSection.id]: subSection },
    pageOrder: [...section.pageOrder, subSection.id],
  });
}

function findSubSection(design: ReportDesign, subSectionId: string): ReportSubSection | undefined {
  const at = locate(design, subSectionId);
  return at ? design.sections[at.sectionId].pages[subSectionId] : undefined;
}

function withSubSection(design: ReportDesign, subSectionId: string, subSection: ReportSubSection): ReportDesign {
  const at = locate(design, subSectionId);
  if (!at) return design;
  const section = design.sections[at.sectionId];
  return withSection(design, at.sectionId, {
    ...section,
    pages: { ...section.pages, [subSectionId]: subSection },
  });
}

/** A block's page, the same rule report-aggregation applies: absent or odd means the first. */
function blockPage(block: PageSection): number {
  const page = block.page;
  return typeof page === 'number' && Number.isFinite(page) && page > 0 ? Math.trunc(page) : 0;
}

/** The subsection holding a block, since the deck addresses blocks by id alone. */
function locateBlock(design: ReportDesign, blockId: string): ReportSubSection | undefined {
  for (const sectionId of design.sectionOrder) {
    const section = design.sections[sectionId];
    for (const subSectionId of section?.pageOrder ?? []) {
      const subSection = section.pages[subSectionId];
      if (subSection?.pageSections[blockId]) return subSection;
    }
  }
  return undefined;
}

function withBlock(design: ReportDesign, subSection: ReportSubSection, block: PageSection): ReportDesign {
  return withSubSection(design, subSection.id, {
    ...subSection,
    pageSections: { ...subSection.pageSections, [block.id]: block },
  });
}

/**
 * A variable not yet in `variableMap` has nothing for the renderer to resolve, and the chart shows
 * "No data" -- so picking one has to record its data position at the same time.
 */
function withMappedVariable(design: ReportDesign, variable: PartVariable): ReportDesign {
  if (!variable.position) return design;
  return { ...design, variableMap: { ...design.variableMap, [variable.key]: variable.position } };
}

// -------------------------------------------------------------------------------------------
// Subsections
// -------------------------------------------------------------------------------------------

/**
 * One role of the deck's colour palette. Every chart colour and most of the deck's chrome resolves
 * from these five, so a change here repaints the whole report -- which is the point: a deck is
 * built to be handed to a client in their colours.
 */
export function setPaletteColor(
  design: ReportDesign,
  role: keyof ColorPalette,
  color: string,
): ReportDesign {
  // A picker that has been cleared hands back an empty string, and a chart with no colour renders
  // black; keeping the previous one is the better failure.
  if (!color?.trim()) return design;
  return { ...design, colorPalette: { ...design.colorPalette, [role]: color.trim() } };
}

/**
 * A theme's colours over the deck's, all five at once (NK-450). Everything else in the design is
 * left as it was -- a theme is only ever a palette as far as the design is concerned.
 */
export function applyThemePalette(design: ReportDesign, palette: ColorPalette): ReportDesign {
  return { ...design, colorPalette: { ...palette } };
}

/**
 * NK-518 — a theme's logo onto the deck, or none where the theme has none, unless the deck uses a
 * logo of its own: what the server's EndClientReportThemes.WithTheme does to the logo. The deck's own
 * `hideLogo` is left alone either way.
 */
export function withThemeLogo(design: ReportDesign, logo: ReportLogo | undefined): ReportDesign {
  if (design.usingCustomLogo) return design;
  const { logo: _, ...rest } = design;
  return logo?.imageId ? { ...rest, logo: { imageId: logo.imageId } } : rest;
}

/** A logo uploaded for this deck alone, which theme updates then leave in place. */
export function withCustomLogo(design: ReportDesign, logo: ReportLogo): ReportDesign {
  return { ...design, logo: { imageId: logo.imageId }, usingCustomLogo: true };
}

/** The deck back on its theme's logo: the custom one dropped, and theme updates free to replace it again. */
export function withoutCustomLogo(design: ReportDesign, themeLogo: ReportLogo | undefined): ReportDesign {
  const { usingCustomLogo: _, ...rest } = design;
  return withThemeLogo(rest, themeLogo);
}

export function renameSubSection(design: ReportDesign, subSectionId: string, name: string): ReportDesign {
  const subSection = findSubSection(design, subSectionId);
  if (!subSection || !name.trim()) return design;
  return withSubSection(design, subSectionId, { ...subSection, name: name.trim() });
}

export function setSubSectionSubheader(design: ReportDesign, subSectionId: string, subheader: string): ReportDesign {
  const subSection = findSubSection(design, subSectionId);
  if (!subSection) return design;

  const trimmed = subheader.trim();
  const next: ReportSubSection = { ...subSection };
  // Absent rather than empty, so the renderer's `@if (subsection.subheader)` stays the only check needed.
  if (trimmed) next.subheader = trimmed;
  else delete next.subheader;

  return withSubSection(design, subSectionId, next);
}

/**
 * The subsection's key takeaway -- the one-line "so what" drawn above its blocks. Stored absent rather
 * than empty for the same reason as the subheader above: the renderer's `@if` is then the only
 * check anything needs, and a cleared takeaway leaves no empty key behind in the saved document.
 */
export function setSubSectionKeyTakeaway(design: ReportDesign, subSectionId: string, takeaway: string): ReportDesign {
  const subSection = findSubSection(design, subSectionId);
  if (!subSection) return design;

  const trimmed = takeaway.trim();
  const next: ReportSubSection = { ...subSection };
  if (trimmed) next.keyTakeaway = trimmed;
  else delete next.keyTakeaway;

  return withSubSection(design, subSectionId, next);
}

export function removeSubSection(design: ReportDesign, subSectionId: string): ReportDesign {
  if (!locate(design, subSectionId)) return design;
  return pruneEmptySections(detachSubSection(design, subSectionId));
}

/**
 * Reorders within a section. Crossing sections is placeSubSection: a drag in the index, or the
 * arrow keys on a subsection's grip at either end of its section.
 */
export function moveSubSection(design: ReportDesign, subSectionId: string, direction: -1 | 1): ReportDesign {
  const at = locate(design, subSectionId);
  if (!at) return design;

  const section = design.sections[at.sectionId];
  const target = at.index + direction;
  if (target < 0 || target >= section.pageOrder.length) return design;

  const pageOrder = [...section.pageOrder];
  [pageOrder[at.index], pageOrder[target]] = [pageOrder[target], pageOrder[at.index]];

  return withSection(design, at.sectionId, { ...section, pageOrder });
}

/**
 * Puts a subsection at a place in the index: in front of `beforeId` in section `sectionId`, or at
 * the end of that section where `beforeId` is null. What dragging one in the index drops, within its
 * section or into another; moveSubSection is the arrow keys on its grip.
 *
 * A section the move empties goes, as it does when its last subsection is deleted. The same design
 * back where the subsection would land where it already is.
 */
export function placeSubSection(
  design: ReportDesign,
  subSectionId: string,
  sectionId: string,
  beforeId: string | null,
): ReportDesign {
  const at = locate(design, subSectionId);
  const subSection = findSubSection(design, subSectionId);
  const section = design.sections[sectionId];
  if (!at || !subSection || !section || beforeId === subSectionId) return design;
  if (beforeId && !section.pageOrder.includes(beforeId)) return design;

  const order = section.pageOrder.filter(id => id !== subSectionId);
  const index = beforeId ? order.indexOf(beforeId) : order.length;
  const pageOrder = [...order.slice(0, index), subSectionId, ...order.slice(index)];
  const unchanged = at.sectionId === sectionId
    && pageOrder.every((id, position) => id === section.pageOrder[position]);
  if (unchanged) return design;

  const detached = at.sectionId === sectionId ? design : detachSubSection(design, subSectionId);
  const target = detached.sections[sectionId];
  return pruneEmptySections(withSection(detached, sectionId, {
    ...target,
    pages: { ...target.pages, [subSectionId]: subSection },
    pageOrder,
  }));
}

/** A whole section one place up or down the index, its subsections with it. */
export function moveSection(design: ReportDesign, sectionId: string, direction: -1 | 1): ReportDesign {
  const at = design.sectionOrder.indexOf(sectionId);
  const target = at + direction;
  if (at === -1 || target < 0 || target >= design.sectionOrder.length) return design;

  const sectionOrder = [...design.sectionOrder];
  [sectionOrder[at], sectionOrder[target]] = [sectionOrder[target], sectionOrder[at]];
  return { ...design, sectionOrder };
}

/**
 * A subsection has to exist before it can be edited in place, and a subsection with no chart renders as nothing
 * -- so a new one arrives carrying a single blank chart, ready to be pointed at a question. Its id is
 * the first `newId` gives, which is how a caller learns it.
 */
function blankSubSection(newId: () => string): ReportSubSection {
  const subSectionId = newId();
  const blockId = newId();
  return {
    id: subSectionId,
    name: 'Untitled subsection',
    pageSections: { [blockId]: emptyBlock(blockId, '', BLANK_CHART_TYPE) },
    pageSectionOrder: [blockId],
  };
}

/**
 * A blank subsection at the end of a section: the one named, from the index's Add subsection under
 * that section, or the last where none is.
 */
export function addBlankSubSection(design: ReportDesign, newId: () => string, sectionId?: string): ReportDesign {
  return attachSubSection(design, blankSubSection(newId), sectionId ? { sectionId } : {}, newId);
}

/**
 * A new section at the end of the deck, holding one blank subsection -- the way a new subsection
 * arrives holding one blank module. It could not arrive empty: a section with nothing in it is
 * removed on the next edit (see pruneEmptySections). The subsection's id is the first `newId` gives.
 */
export function addSection(design: ReportDesign, newId: () => string, name = 'New section'): ReportDesign {
  return attachSubSection(design, blankSubSection(newId), { newSectionName: name }, newId);
}

export function renameSection(design: ReportDesign, sectionId: string, name: string): ReportDesign {
  const section = design.sections[sectionId];
  if (!section || !name.trim()) return design;
  return withSection(design, sectionId, { ...section, name: name.trim() });
}

// -------------------------------------------------------------------------------------------
// Blocks
// -------------------------------------------------------------------------------------------

/**
 * What a chart with no question yet is drawn as. A bar takes any number of options and any question
 * kind, so it is the one type that cannot be wrong before a question is chosen.
 */
const BLANK_CHART_TYPE: ReportType = 'bar-chart';

/** The settings every type has, carried across a type change. */
type SharedSettings = Pick<BlockPatch, 'sortDescending' | 'topN'>;

function sharedSettingsOf(block: PageSection): SharedSettings {
  // Methodology declares neither, so narrow it out before the destructure rather than making it
  // carry two fields it has no use for.
  if (chartsNothing(block)) return {};

  // An open-end has no answer list, so neither setting exists on it -- and carrying one across a
  // type change would write a sort onto a module that cannot honour it. Nor does a heatmap.
  if (block.reportType === 'open-end-explorer' || block.reportType === 'word-cloud'
    || block.reportType === 'heatmap') return {};

  // See seriesForBlock: these modules declare neither on purpose.
  const { sortDescending, topN } = keepsGivenOrder(block)
    ? { sortDescending: undefined, topN: undefined }
    : block.reportExtraData;
  return {
    ...(sortDescending === undefined ? {} : { sortDescending }),
    ...(topN === undefined ? {} : { topN }),
  };
}

interface TypeExtras {
  orientation?: ChartOrientation;
  columns?: number;
  includeTotal?: boolean;
  groupBy?: string;
}

/**
 * Exhaustive on reportType, with a `never` default, because each type's `reportExtraData` is a
 * different shape and a missed case would silently produce the wrong one.
 */
function emptyBlock(
  id: string,
  variableKey: string,
  reportType: ReportType,
  segmentLocation: SegmentLocation = 'full',
  settings: SharedSettings = {},
  extra: TypeExtras = {},
): PageSection {
  const base = { id, segmentLocation };
  const oriented = extra.orientation ? { orientation: extra.orientation } : {};

  switch (reportType) {
    // No variable and no shared settings -- these chart nothing.
    case 'study-overview':
    case 'headline-stats':
    // Added empty on purpose: every word of it is a recommendation, which is written by hand.
    case 'action-list':
    // Written in place, or uploaded from the settings panel: nothing to start from.
    case 'text':
    case 'image':
      return { ...base, reportType, reportExtraData: {} };
    // Takes the question, so a dial added over one computes straight away -- but none of the shared
    // settings, since it neither sorts nor trims: its bands are counted off the ends of the answer
    // list and a sort would move answers between them.
    case 'kpi-score':
      return { ...base, reportType, reportExtraData: { variableKey } };
    case 'pie-chart':
      return { ...base, reportType, reportExtraData: { variableKey, ...settings } };
    case 'donut-chart':
      return { ...base, reportType, reportExtraData: { variableKey, ...settings } };
    case 'filter-comparison':
      return { ...base, reportType, reportExtraData: { variableKey, ...settings, ...oriented } };
    case 'stacked-scale':
      return { ...base, reportType, reportExtraData: { variableKeys: [variableKey] } };
    case 'funnel':
      return { ...base, reportType, reportExtraData: { variableKeys: [variableKey] } };
    case 'diverging-bars':
      return { ...base, reportType, reportExtraData: { variableKeys: [variableKey] } };
    // One axis to start with; the block says it needs a second until one is chosen.
    case 'scatter-plot':
      return { ...base, reportType, reportExtraData: { variableKeys: [variableKey] } };
    // The priority column is optional, so one attribute question is enough to draw with.
    case 'ownership-matrix':
      return { ...base, reportType, reportExtraData: { variableKeys: [variableKey] } };
    case 'stage-matrix':
      return {
        ...base,
        reportType,
        reportExtraData: { variableKeys: [variableKey], ...exampleLadder() },
      };
    // Seeded rather than empty, unlike the recommended actions: neither of these has a shape of its
    // own until something is in it, so a hand-added one starts as a worked example to edit rather
    // than a blank rectangle. See block-examples.ts for why they are examples and not defaults.
    case 'stage-explainer':
      return { ...base, reportType, reportExtraData: exampleExplainer() };
    // No shared settings: an open-end has no answer list to sort or trim, and every setting on
    // these two defaults inside the block.
    case 'open-end-explorer':
    case 'word-cloud':
    // Draws with its question alone: the survey's image comes with the data.
    case 'heatmap':
      return { ...base, reportType, reportExtraData: { variableKey } };
    // Every setting defaults inside the block, so one row of the grid is enough to draw with.
    case 'tiered-ranking':
      return { ...base, reportType, reportExtraData: { variableKeys: [variableKey] } };
    case 'bar-chart':
      return {
        ...base,
        reportType,
        reportExtraData: { variableKeys: [variableKey], ...settings, ...oriented },
      };
    case 'segment-tiles':
      return {
        ...base,
        reportType,
        reportExtraData: {
          variableKey,
          groupBy: extra.groupBy ?? '',
          ...settings,
          ...(extra.includeTotal === undefined ? {} : { includeTotal: extra.includeTotal }),
        },
      };
    case 'subgroup-panels':
      return {
        ...base,
        reportType,
        reportExtraData: {
          variableKey,
          // Empty until one is chosen; the block says so rather than rendering a lone Total panel.
          groupBy: extra.groupBy ?? '',
          ...settings,
          ...oriented,
          ...(extra.columns === undefined ? {} : { columns: extra.columns }),
          ...(extra.includeTotal === undefined ? {} : { includeTotal: extra.includeTotal }),
        },
      };
    case 'crosstab':
      return {
        ...base,
        reportType,
        reportExtraData: {
          variableKey,
          // Empty until one is chosen; the block then draws a Total column and nothing else, which
          // is a legible "pick the columns" rather than a broken table.
          groupBy: extra.groupBy ?? '',
          ...settings,
          ...(extra.includeTotal === undefined ? {} : { includeTotal: extra.includeTotal }),
        },
      };
    default:
      throw new Error(`Unhandled report type: ${reportType satisfies never}`);
  }
}

/**
 * Every variable a block charts. Mirrors blockVariableKeys rather than importing it: that module
 * pulls in this one's siblings, and the builder rejects a cycle.
 */
function keysOf(block: PageSection): string[] {
  if (chartsNothing(block)) return [];
  return chartsManyVariables(block)
    ? block.reportExtraData.variableKeys
    : [block.reportExtraData.variableKey];
}

/**
 * Modules that chart no question, in one place rather than a `||` chain repeated at each gate below.
 * A mirror of report-aggregation's blockVariableKeys, which cannot be imported here --
 * report-design-edit -> chart-layout -> report-aggregation is a cycle at runtime. That switch is the
 * source of truth and is exhaustive, so a new module is forced to decide there first.
 */
const CHARTS_NOTHING = ['study-overview', 'headline-stats', 'action-list',
  'stage-explainer', 'text', 'image'] as const satisfies readonly ReportType[];

/**
 * Modules whose variable order is the finding, so neither sorting nor a top-N is ever written onto
 * them. A mirror of report-aggregation's `keepsGivenOrder`, which cannot be imported here --
 * report-design-edit -> chart-layout -> report-aggregation is a cycle at runtime.
 */
const KEEPS_GIVEN_ORDER = ['stacked-scale', 'funnel', 'diverging-bars', 'tiered-ranking',
  'scatter-plot', 'ownership-matrix', 'stage-matrix',
  'kpi-score'] as const satisfies readonly ReportType[];

type OrderedType = (typeof KEEPS_GIVEN_ORDER)[number];

/** A type guard rather than a plain test, so the list stays single and the union still narrows. */
function keepsGivenOrder(block: PageSection): block is Extract<PageSection, { reportType: OrderedType }> {
  return KEEPS_GIVEN_ORDER.includes(block.reportType as OrderedType);
}

/**
 * Modules that chart a list of variables rather than exactly one. A mirror of report-aggregation's
 * `acceptsMultipleVariables`, for the same cycle reason as CHARTS_NOTHING.
 */
const MULTI_VARIABLE = ['bar-chart', 'stacked-scale', 'funnel', 'diverging-bars', 'tiered-ranking',
  'scatter-plot', 'ownership-matrix', 'stage-matrix'] as const satisfies readonly ReportType[];

type MultiVariableType = (typeof MULTI_VARIABLE)[number];

function chartsManyVariables(
  block: PageSection,
): block is Extract<PageSection, { reportType: MultiVariableType }> {
  return MULTI_VARIABLE.includes(block.reportType as MultiVariableType);
}

type ChartlessType = (typeof CHARTS_NOTHING)[number];

function typeChartsNothing(reportType: ReportType): boolean {
  return (CHARTS_NOTHING as readonly ReportType[]).includes(reportType);
}

/**
 * A type predicate rather than a plain boolean: the callers below reach into `reportExtraData`
 * afterwards, and only a predicate narrows the union in both branches. An indexed lookup does not.
 */
function chartsNothing(block: PageSection): block is Extract<PageSection, { reportType: ChartlessType }> {
  return typeChartsNothing(block.reportType);
}

/** A block setting the editor can change. Exported so the editor can ask what applies where. */
export type BlockField = (typeof PATCHABLE)[number];

const PATCHABLE = [
  'orientation', 'columns', 'includeTotal', 'sortDescending', 'topN', 'groupBy', 'valueUnit',
  'actions',
  'hideLabels', 'hideEmptyAnswers', 'rungNames', 'showNet', 'netLabel', 'netBoxes', 'netThreshold', 'netTopBox', 'barOrder', 'showShare', 'shareCaption', 'showDropOff', 'countedAnswers', 'subgroups',
  'cellValue', 'showCounts', 'sigTest', 'nets', 'showMean', 'meanExcludes', 'shading', 'layerView', 'leftAnswer', 'leftLabel', 'rightLabel', 'gapLabel', 'showGap',
  'topRanks', 'rankOneEnd', 'tierCount', 'tierLabels', 'categories',
  'pageSize', 'searchable',
  'maxWords', 'minCount', 'stopwords', 'useQuestionWords',
  'imageId', 'showOptOut', 'sharedScale',
  'xLabel', 'yLabel', 'showQuadrants', 'quadrantLabel',
  'priorityLabel', 'showWhiteSpace', 'whiteSpaceLabel', 'rowOrder',
  'stages', 'totalLabel', 'totalExcludes', 'spectrum', 'panels',
  'sampleSize', 'medianLoi', 'incidenceRate', 'market', 'notesHeading', 'notes',
  'boxes', 'takeaways',
  'value', 'label', 'scaleMax', 'scaleMin', 'metric', 'shareBand', 'bands', 'topBand', 'netExclude',
  'display', 'caption',
  'html', 'altText',
] as const;

/**
 * Whether a setting means anything for this module. `updateBlock` drops one that does not, and the
 * editor asks the same question before offering the control -- so a setting is never shown where
 * changing it would be quietly thrown away.
 */
export function fieldAppliesTo(reportType: ReportType, field: BlockField): boolean {
  switch (field) {
    case 'columns':
      return reportType === 'subgroup-panels';
    case 'groupBy':
    case 'includeTotal':
    case 'subgroups':
      // The three modules that split a question across subgroups of respondents. A table's columns
      // are the same choice as a grid's panels and a map's tiles, stored the same way.
      return reportType === 'subgroup-panels' || reportType === 'segment-tiles'
        || reportType === 'crosstab';
    case 'cellValue':
    case 'showCounts':
    case 'sigTest':
    case 'nets':
    case 'showMean':
    case 'meanExcludes':
    case 'shading':
    case 'layerView':
      // Only a table has a cell to put something in. A chart's mark is the figure.
      return reportType === 'crosstab';
    case 'leftAnswer':
    case 'leftLabel':
    case 'rightLabel':
    case 'gapLabel':
    case 'showGap':
      return reportType === 'diverging-bars';
    case 'tierCount':
    case 'tierLabels':
      return reportType === 'tiered-ranking';
    // Every module that draws a row per question. On the first two a category takes the bars'
    // colour; on a head-to-head it cannot -- the two colours there already say which side won -- so
    // it gathers the rows into named groups instead. Same field, read two ways.
    case 'categories':
      return reportType === 'tiered-ranking' || reportType === 'bar-chart'
        || reportType === 'diverging-bars';
    // Shared with the ownership matrix's priority column, which reads a ranking grid the same way.
    case 'topRanks':
    case 'rankOneEnd':
      return reportType === 'tiered-ranking' || reportType === 'ownership-matrix';
    case 'pageSize':
    case 'searchable':
      return reportType === 'open-end-explorer';
    case 'maxWords':
    case 'minCount':
    case 'stopwords':
    case 'useQuestionWords':
      return reportType === 'word-cloud';
    // An upload in place of a heatmap's creative, or an image module's picture.
    case 'imageId':
      return reportType === 'heatmap' || reportType === 'image';
    case 'showOptOut':
    case 'sharedScale':
      return reportType === 'heatmap';
    case 'html':
      return reportType === 'text';
    case 'altText':
      return reportType === 'image';
    case 'xLabel':
    case 'yLabel':
    case 'showQuadrants':
    case 'quadrantLabel':
      return reportType === 'scatter-plot';
    case 'priorityLabel':
    case 'showWhiteSpace':
    case 'whiteSpaceLabel':
    case 'rowOrder':
      return reportType === 'ownership-matrix';
    case 'stages':
    case 'totalLabel':
    case 'totalExcludes':
      return reportType === 'stage-matrix';
    case 'panels':
      return reportType === 'stage-explainer';
    // The band of captions is drawn by both, and means the same thing on each.
    case 'spectrum':
      return reportType === 'stage-matrix' || reportType === 'stage-explainer';
    case 'orientation':
      return reportType === 'bar-chart'
        || reportType === 'filter-comparison'
        || reportType === 'subgroup-panels';
    case 'sampleSize':
    case 'medianLoi':
    case 'incidenceRate':
    case 'market':
      return reportType === 'study-overview';
    case 'actions':
      return reportType === 'action-list';
    case 'valueUnit':
      // Bars are the only shape a numeric battery renders as, so it is the only type with a unit
      // to choose. The control itself only appears once such a question is charted.
      return reportType === 'bar-chart';
    // The word a net is *of*, shared by the two modules that print one: "Net Agree" down a
    // battery's side, "Net Momentum" over a single figure.
    case 'netLabel':
      return reportType === 'stacked-scale' || reportType === 'kpi-score';
    case 'showNet':
    case 'netBoxes':
    case 'netThreshold':
    case 'netTopBox':
    case 'barOrder':
    case 'showShare':
    case 'shareCaption':
      // Only where the answer order carries meaning, which is the one module that asserts it does.
      // `netBoxes` and `netTopBox` are the older way of saying what `bands` and `topBand` now say;
      // still honoured when a block has no bands, so a design written before them keeps its figures.
      return reportType === 'stacked-scale';
    case 'rungNames':
      return reportType === 'stage-matrix';
    case 'showDropOff':
      return reportType === 'funnel';
    case 'countedAnswers':
      // Every module that reduces a whole question to a single figure, and only those: a module
      // that draws the distribution has no answer to choose, because it draws all of them.
      return reportType === 'segment-tiles' || reportType === 'funnel';
    case 'hideEmptyAnswers':
      // The one module that keeps a zero on its axis: a pie has no slice to draw for one, and the
      // modules that score a scale keep every answer because they count by position.
      return reportType === 'bar-chart';
    case 'hideLabels':
      // Nothing here is labelled; the answers, the words or the image are the content.
      if (reportType === 'open-end-explorer' || reportType === 'word-cloud'
        || reportType === 'heatmap') return false;
      // A dot with no name is a position belonging to nothing, and a matrix is all labels.
      if (reportType === 'scatter-plot' || reportType === 'ownership-matrix'
        || reportType === 'stage-matrix') return false;
      // A market tile is its name -- hiding it leaves a coloured square and a number belonging to
      // nothing. A table's row labels and a trade-off's are the same: percentages against nothing.
      if (reportType === 'segment-tiles' || reportType === 'crosstab'
        || reportType === 'diverging-bars' || reportType === 'tiered-ranking') return false;
      // On a dial it means the line spelling out what the score is a net of, which is the only
      // label it has -- a different thing from a chart's answer labels, under one name because it is
      // one control saying "do not print the explanation".
      // Every module that draws a chart. The three that draw none have no labels to hide.
      return !typeChartsNothing(reportType);
    case 'sortDescending':
    case 'topN':
      // Both would reorder or truncate an order that is itself the finding -- a scale, a journey.
      if (KEEPS_GIVEN_ORDER.includes(reportType as OrderedType)) return false;
      // An open-end has no answer list to sort or trim: the verbatim explorer's subsection size and the
      // cloud's word limit do that job themselves. A heatmap has no list at all.
      if (reportType === 'open-end-explorer' || reportType === 'word-cloud'
        || reportType === 'heatmap') return false;
      // Every module that charts something: the rest declare neither, and the default arm below
      // would otherwise write both onto them.
      return !typeChartsNothing(reportType);
    case 'notesHeading':
    case 'notes':
      return reportType === 'study-overview';
    case 'boxes':
    case 'takeaways':
      return reportType === 'headline-stats';
    case 'value':
    case 'label':
    case 'scaleMax':
    case 'scaleMin':
    case 'metric':
    case 'display':
    case 'caption':
      return reportType === 'kpi-score';
    // Which band a share reads: the dial's figure, or a single stacked bar's headline.
    case 'shareBand':
      return reportType === 'kpi-score' || reportType === 'stacked-scale';
    // The band vocabulary, shared by the module that reduces a scale to one figure, the one that
    // draws the same scale underneath it, and the one that nets it bar by bar -- so a score, its
    // distribution and a battery's net column all mean the same thing by "top" and "bottom".
    case 'bands':
    case 'topBand':
      return reportType === 'kpi-score' || reportType === 'bar-chart'
        || reportType === 'stacked-scale';
    // Which answers are off the scale. The stacked distribution has had it all along, written by the
    // agent rather than the editor; the dial needs the same exclusion for the same reason.
    case 'netExclude':
      return reportType === 'kpi-score' || reportType === 'stacked-scale';
    default:
      return true;
  }
}

/**
 * Patches one block's editable fields. Only keys present in `patch` are touched, so a field this
 * caller never mentions survives -- which is the whole point of the shape. A field the target type
 * has no place for is dropped rather than written as dead weight.
 */
export function updateBlock(design: ReportDesign, blockId: string, patch: BlockPatch): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block) return design;

  const next = {
    ...block,
    ...(patch.segmentLocation ? { segmentLocation: patch.segmentLocation } : {}),
    reportExtraData: { ...block.reportExtraData },
  } as PageSection;

  if (patch.header !== undefined) {
    const header = patch.header.trim();
    if (header) (next as { header?: string }).header = header;
    else delete (next as { header?: string }).header;
  }

  // Written through a mutable view: reportExtraData is a discriminated union, so assigning a partial
  // to it wholesale does not narrow, while per-key writes on the copied object do.
  const data = next.reportExtraData as unknown as Record<string, unknown>;
  for (const field of PATCHABLE) {
    if (patch[field] === undefined) continue;
    if (!fieldAppliesTo(next.reportType, field)) continue;
    data[field] = patch[field];
  }

  return withBlock(design, subSection, next);
}

/** Changes the charted question, recording its data position when it is not yet mapped. */
export function setBlockVariable(
  design: ReportDesign,
  blockId: string,
  variable: PartVariable,
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  // These chart no question, so there is nothing here to point at one.
  if (!subSection || !block || chartsNothing(block)) return design;

  const next = { ...block, reportExtraData: { ...block.reportExtraData } } as PageSection;
  const data = next.reportExtraData as unknown as Record<string, unknown>;
  if (chartsManyVariables(next)) {
    data['variableKeys'] = [variable.key];
  } else {
    data['variableKey'] = variable.key;
  }

  return withBlock(withMappedVariable(design, variable), subSection, next);
}

/**
 * Changes the question a block groups by, recording its data position the same way picking the
 * charted question does.
 *
 * It has to go through here rather than through `updateBlock`: a plain patch writes the key it is
 * given, and a question not already in `variableMap` has nothing for the renderer to resolve -- so
 * choosing a grouping question that had never been charted produced a block with no panels and no
 * explanation, which is exactly what a bare `groupBy` patch cannot avoid.
 */
/**
 * The ranking grid behind the ownership matrix's priority column.
 *
 * Its own function for the reason `setBlockGroupBy` is: a field naming variables has to record
 * their data positions in `variableMap`, and a generic patch has nowhere to carry a position. A
 * question not already mapped would otherwise resolve to nothing and the column would read empty.
 */
export function setBlockPriority(
  design: ReportDesign,
  blockId: string,
  variables: PartVariable[],
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block || block.reportType !== 'ownership-matrix') return design;

  const next = { ...block, reportExtraData: { ...block.reportExtraData } } as PageSection;
  const data = next.reportExtraData as unknown as Record<string, unknown>;
  data['priorityKeys'] = variables.map(variable => variable.key);

  const mapped = variables.reduce(withMappedVariable, design);
  return withBlock(mapped, subSection, next);
}

export function setBlockGroupBy(
  design: ReportDesign,
  blockId: string,
  variable: PartVariable,
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block || !fieldAppliesTo(block.reportType, 'groupBy')) return design;

  const next = { ...block, reportExtraData: { ...block.reportExtraData } } as PageSection;
  const data = next.reportExtraData as unknown as Record<string, unknown>;
  data['groupBy'] = variable.key;
  // The panel choice names answers of the old question, which the new one has never heard of.
  data['subgroups'] = [];

  return withBlock(withMappedVariable(design, variable), subSection, next);
}

/**
 * Replaces the panel/tile/column list, recording the data position of every question it names.
 *
 * Separate from a plain `subgroups` patch because of what a patch cannot do: adding a second
 * question to the list is a reference to a question the deck may never have charted, and a key
 * that is not in `variableMap` resolves to nothing at all -- `filterOptions` returns no answers, so
 * the editor draws the question with no tick list and the renderer draws no column for it. That was
 * the bug: the question could be added and then neither seen nor changed.
 *
 * Every referenced question is mapped rather than only the new one, so a list that arrives with an
 * unmapped question -- a generated design, or one whose grouping question changed underneath it --
 * is repaired by the next edit rather than staying broken.
 */
export function setBlockSubgroups(
  design: ReportDesign,
  blockId: string,
  subgroups: { variableKey: string; answer: string }[],
  variables: PartVariable[],
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block || !fieldAppliesTo(block.reportType, 'subgroups')) return design;

  const next = { ...block, reportExtraData: { ...block.reportExtraData } } as PageSection;
  (next.reportExtraData as unknown as Record<string, unknown>)['subgroups'] = subgroups;

  const mapped = variables.reduce(withMappedVariable, design);
  return withBlock(mapped, subSection, next);
}

/**
 * Restricts one table to respondents who gave one of the listed answers, recording the data position
 * of every question it names -- the same reason `setBlockSubgroups` exists: a question nobody has
 * charted is not in `variableMap`, and an unmapped key filters to nobody. An empty list removes it.
 */
export function setBlockTableFilter(
  design: ReportDesign,
  blockId: string,
  tableFilter: { variableKey: string; answer: string }[],
  variables: PartVariable[],
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block || block.reportType !== 'crosstab') return design;

  const data = { ...block.reportExtraData };
  if (tableFilter.length) data.tableFilter = tableFilter;
  else delete data.tableFilter;

  const mapped = variables.reduce(withMappedVariable, design);
  return withBlock(mapped, subSection, { ...block, reportExtraData: data });
}

/** Every question a crosstab names, so a copy of it can be mapped wherever it goes. */
export function crosstabKeys(block: Extract<PageSection, { reportType: 'crosstab' }>): string[] {
  const extra = block.reportExtraData;
  return [...new Set([
    extra.variableKey,
    extra.groupBy,
    extra.layerBy ?? '',
    ...(extra.subgroups ?? []).map(subgroup => subgroup.variableKey),
    ...(extra.tableFilter ?? []).map(entry => entry.variableKey),
  ].filter(Boolean))];
}

/** A block wherever it is in the deck. */
export function locateBlockIn(design: ReportDesign, blockId: string): PageSection | undefined {
  return locateBlock(design, blockId)?.pageSections[blockId];
}

/**
 * A crosstab's third variable: the question whose answers slice the whole table. Mapped like any other
 * question the table names. Null removes it.
 */
export function setBlockLayer(
  design: ReportDesign,
  blockId: string,
  variable: PartVariable | null,
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block || block.reportType !== 'crosstab') return design;

  const data = { ...block.reportExtraData };
  if (variable?.key) data.layerBy = variable.key;
  else delete data.layerBy;

  const mapped = variable?.key ? withMappedVariable(design, variable) : design;
  return withBlock(mapped, subSection, { ...block, reportExtraData: data });
}

/**
 * A crosstab placed straight after another block, full width: how a reader's drilled-in view of a
 * table is kept, as a table of its own that every later reader of the deck sees. Every question it
 * names is mapped, since a view can reach questions the deck never charted.
 */
export function insertCrosstabAfter(
  design: ReportDesign,
  afterBlockId: string,
  block: Extract<PageSection, { reportType: 'crosstab' }>,
  variables: PartVariable[],
): ReportDesign {
  const subSection = locateBlock(design, afterBlockId);
  const after = subSection?.pageSections[afterBlockId];
  if (!subSection || !after) return design;

  const placed = { ...block, segmentLocation: 'full' as SegmentLocation, page: after.page };
  const order = [...subSection.pageSectionOrder];
  order.splice(order.indexOf(afterBlockId) + 1, 0, block.id);
  const mapped = variables.reduce(withMappedVariable, design);
  return withSubSection(mapped, subSection.id, {
    ...(findSubSection(mapped, subSection.id) ?? subSection),
    pageSections: { ...subSection.pageSections, [block.id]: placed },
    pageSectionOrder: order,
  });
}

/**
 * The deck's statistics: weighting, confidence, minimum base and table decimals. Every question a
 * target weighting names is mapped, so a published bundle packs its answers -- a weight worked out in
 * the browser needs the same respondents' answers there that it had in the editor.
 */
export function setReportStatistics(
  design: ReportDesign,
  statistics: ReportStatistics,
  variables: PartVariable[] = [],
): ReportDesign {
  const mapped = variables.reduce(withMappedVariable, design);
  if (Object.values(statistics).every(value => value === undefined)) {
    const rest = { ...mapped };
    delete rest.statistics;
    return rest;
  }
  return { ...mapped, statistics };
}

/** Replaces the whole list of charted variables, for the types that draw several. */
export function setBlockVariables(
  design: ReportDesign,
  blockId: string,
  variables: PartVariable[],
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  const multiVariable = !!block && chartsManyVariables(block);
  if (!subSection || !block || !multiVariable || !variables.length) return design;

  const mapped = variables.reduce(withMappedVariable, design);
  return withBlock(mapped, subSection, {
    ...block,
    reportExtraData: { ...block.reportExtraData, variableKeys: variables.map(v => v.key) },
  });
}

/** Converts a block to another type, carrying across whatever the target shape has room for. */
export function setBlockType(
  design: ReportDesign,
  blockId: string,
  reportType: ReportType,
): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block || block.reportType === reportType) return design;

  const data = block.reportExtraData as unknown as Record<string, unknown>;
  const converted = emptyBlock(
    block.id,
    keysOf(block)[0] ?? '',
    reportType,
    block.segmentLocation,
    sharedSettingsOf(block),
    {
      orientation: data['orientation'] as ChartOrientation | undefined,
      columns: data['columns'] as number | undefined,
      includeTotal: data['includeTotal'] as boolean | undefined,
      groupBy: data['groupBy'] as string | undefined,
    },
  );

  return withBlock(design, subSection, block.header ? { ...converted, header: block.header } : converted);
}

/**
 * A blank chart at the top of the subsection: no question yet, so it lands where it will be seen and
 * configured rather than below the charts already there.
 */
/**
 * A new explainer's settings: the ladder's stages and names, with the example's prose filled in for
 * any rung it recognises. Only ever used at creation -- after that the panels are derived on every
 * render and the points are whatever has been written on the block.
 */
function seededExplainer(ladder: StageMatrixExtraData, written?: string[][]): StageExplainerExtraData {
  if (written?.length) return writtenExplainer(ladder, written);
  // The names the ladder carries, as a stand-in for the rungs it will draw: this runs when the
  // explainer is created, before any data is in hand. Whatever it writes is replaced on the first
  // render by the real thing -- see asDrawn -- so this only has to be close enough to seed points.
  const derived = explainerFromLadder(ladder, undefined, ladder.rungNames ?? []);
  return {
    ...derived,
    panels: derived.panels.map(panel => ({
      ...panel,
      parts: (panel.parts ?? []).map(part => ({
        ...part,
        points: EXAMPLE_PART_POINTS[part.heading] ?? [],
      })),
    })),
  };
}

/**
 * A new explainer carrying the points the report-generation agent wrote on its matrix, one list per
 * rung, left to right.
 *
 * Laid out by the stages' spans rather than by the rungs' names, which is all it has to get right:
 * every render re-derives the headings from the rungs the matrix actually draws, and keeps only the
 * stored points, matched by stage and by part within it (see panelsFromLadder). Going through the
 * names instead -- as the seed above does -- would lose every point on a matrix the agent left
 * unnamed, since a stage with no named rungs has no parts to hold them.
 */
function writtenExplainer(ladder: StageMatrixExtraData, written: string[][]): StageExplainerExtraData {
  let at = 0;
  const panels = (ladder.stages ?? []).map(stage => {
    const span = Math.max(0, Math.trunc(stage.rungs));
    const parts = Array.from({ length: span }, (_, offset) => ({
      heading: ladder.rungNames?.[at + offset]?.trim() ?? '',
      points: (written[at + offset] ?? []).map(point => point.trim()).filter(Boolean),
    }));
    at += span;
    return { title: stage.label, parts };
  });
  return { panels, spectrum: ladder.spectrum };
}

/** Where a ladder's explainer sits, if it has one. */
export type ExplainerPlacement = 'none' | 'before' | 'after';

/** The explainer paired with this ladder: the one in the same subsection, if there is one. */
export function explainerFor(design: ReportDesign, ladderBlockId: string): PageSection | undefined {
  const subSection = locateBlock(design, ladderBlockId);
  if (!subSection) return undefined;
  return subSection.pageSectionOrder
    .map(id => subSection.pageSections[id])
    .find(block => block?.reportType === 'stage-explainer');
}

/** Read back off the two blocks' pages, because the placement is not stored anywhere else. */
export function explainerPlacement(design: ReportDesign, ladderBlockId: string): ExplainerPlacement {
  const subSection = locateBlock(design, ladderBlockId);
  const ladder = subSection?.pageSections[ladderBlockId];
  const explainer = explainerFor(design, ladderBlockId);
  if (!ladder || !explainer) return 'none';
  return blockPage(explainer) <= blockPage(ladder) ? 'before' : 'after';
}

/**
 * Puts a ladder's explainer before it, after it, or nowhere.
 *
 * The explainer is not a module you add -- it is the other half of a ladder, reading its headings
 * from these stages -- so this is the only thing that creates or destroys one.
 *
 * `before` is the usual reading order: meet the vocabulary, then the grid. `after` is for a deck
 * that shows the figures first and explains them on the way out. Moving between the two moves the
 * existing block rather than rebuilding it, so the bullet points written on it survive.
 *
 * Going to `none` takes the block and its bullets with it. They describe stages this deck no longer
 * shows, and keeping an invisible copy to restore later is a worse surprise than retyping them.
 */
export function setLadderExplainer(
  design: ReportDesign,
  ladderBlockId: string,
  placement: ExplainerPlacement,
  newId: () => string,
  /** The points to write under each rung, if this creates the explainer. See withMatrixExplainers. */
  written?: string[][],
): ReportDesign {
  const subSection = locateBlock(design, ladderBlockId);
  const ladder = subSection?.pageSections[ladderBlockId];
  if (!subSection || !ladder || ladder.reportType !== 'stage-matrix') return design;

  const existingId = subSection.pageSectionOrder
    .find(id => subSection.pageSections[id]?.reportType === 'stage-explainer');

  if (placement === 'none') {
    if (!existingId) return design;
    const { [existingId]: _removed, ...kept } = subSection.pageSections;
    return withSubSection(design, subSection.id, {
      ...subSection,
      pageSections: kept,
      pageSectionOrder: subSection.pageSectionOrder.filter(id => id !== existingId),
    });
  }

  // Before the ladder needs a page in front of it to use; after it never does.
  const ladderPage = placement === 'before' ? Math.max(1, blockPage(ladder)) : blockPage(ladder);
  const explainerPage = placement === 'before' ? ladderPage - 1 : ladderPage + 1;

  const explainer: PageSection = existingId
    ? { ...subSection.pageSections[existingId], page: explainerPage }
    : {
      id: newId(),
      // Named after the ladder it explains where that has a name, because a deck reads better
      // saying what is being read than saying "Explainer". Editable like any other block header.
      header: ladder.header?.trim() ? `How to read the ${ladder.header.trim()}` : 'How to read this',
      segmentLocation: 'full',
      page: explainerPage,
      reportType: 'stage-explainer',
      reportExtraData: seededExplainer(ladder.reportExtraData, written),
    };

  const order = subSection.pageSectionOrder.filter(id => id !== explainer.id);
  return withSubSection(design, subSection.id, {
    ...subSection,
    pageSections: {
      ...subSection.pageSections,
      [ladderBlockId]: { ...ladder, page: ladderPage },
      [explainer.id]: explainer,
    },
    // Stored in the order they are read in, so the document matches the deck.
    pageSectionOrder: placement === 'before' ? [explainer.id, ...order] : [...order, explainer.id],
  });
}

/**
 * The design with every stage matrix's `explainer` setting carried out: the explainer it asks for
 * created beside it, exactly as choosing it from the Explainer setting would, and the setting taken
 * off the matrix.
 *
 * The setting is how the report-generation agent turns an explainer on. It cannot add one as a
 * module, because an explainer is not one -- see stage-explainer.ts, which is left out of the
 * agent's catalogue for that reason. It is an instruction rather than a state, so it goes once acted
 * on: where the explainer sits is read off the two blocks' pages (see explainerPlacement), and a
 * second record of it on the matrix would disagree with the setting the first time somebody moved
 * or removed the explainer.
 *
 * A matrix whose subsection already has an explainer only loses the setting -- an edit asking again
 * for one that is there. The same object back when no matrix carries the setting, so a deck saved
 * since costs nothing to read.
 */
export function withMatrixExplainers<T extends ReportDesign | null | undefined>(
  design: T,
  newId: () => string,
): T {
  if (!design?.sections) return design;

  let result: ReportDesign = design;
  for (const section of Object.values(design.sections)) {
    for (const subSection of Object.values(section?.pages ?? {})) {
      for (const [blockId, block] of Object.entries(subSection?.pageSections ?? {})) {
        if (block?.reportType !== 'stage-matrix' || !block.reportExtraData?.explainer) continue;

        const { explainer, ...settings } = block.reportExtraData;
        const holder = locateBlock(result, blockId);
        if (!holder) continue;
        result = withSubSection(result, holder.id, {
          ...holder,
          pageSections: { ...holder.pageSections, [blockId]: { ...block, reportExtraData: settings } },
        });
        if (explainerFor(result, blockId)) continue;

        // Read defensively: this is stored JSON an agent wrote, not something the editor produced.
        const placement = explainer.placement === 'after' ? 'after' : 'before';
        const written = Array.isArray(explainer.points)
          ? explainer.points.map(points => (Array.isArray(points) ? points.filter(isText) : []))
          : undefined;
        result = setLadderExplainer(result, blockId, placement, newId, written);
      }
    }
  }
  return result as T;
}

function isText(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * A new, unconfigured block at the end of the page being looked at.
 *
 * Appended, not prepended: a new module used to arrive above everything already written, so adding
 * a fourth chart meant moving it down past three. The end is where the reader's eye is when they
 * decide they want another one.
 *
 * `page` is carried because a block with none is on the first page -- without it, a module added
 * while looking at page 2 would appear on page 1 and seem not to have been added at all.
 */
export function addBlock(
  design: ReportDesign,
  subSectionId: string,
  newId: () => string,
  page = 0,
): ReportDesign {
  const subSection = findSubSection(design, subSectionId);
  if (!subSection) return design;

  const blockId = newId();
  const block: PageSection = { ...emptyBlock(blockId, '', BLANK_CHART_TYPE), page };
  return withSubSection(design, subSectionId, {
    ...subSection,
    pageSections: { ...subSection.pageSections, [blockId]: block },
    pageSectionOrder: [...subSection.pageSectionOrder, blockId],
  });
}

/**
 * Moves a block one **row** up or down, not one entry.
 *
 * Rows are derived from the order rather than stored -- `layoutRows` pairs a `left` with the `right`
 * that follows it, and gives a `full` block a row of its own -- so swapping two neighbouring entries
 * moved a full-width module *into* a pair, landing it between the two halves and pushing one under
 * the other. What a reader sees move is a row, so that is what moves.
 *
 * Built from `layoutRows` rather than from a second reading of the same rule, because two
 * implementations of "what is a row" is exactly how the editor and the renderer end up disagreeing.
 */
export function moveBlock(design: ReportDesign, blockId: string, direction: -1 | 1): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block) return design;

  // One page at a time: the order holds every page's blocks, and a row on page 2 is no neighbour of
  // a row on page 1.
  const page = blockPage(block);
  const onPage = subSection.pageSectionOrder
    .filter(id => subSection.pageSections[id] && blockPage(subSection.pageSections[id]) === page);

  const rows = layoutRows(subSection, page)
    .map(row => [row.full, row.left, row.right].filter(Boolean).map(entry => entry!.id));

  const at = rows.findIndex(row => row.includes(blockId));
  const target = at + direction;
  if (at === -1 || target < 0 || target >= rows.length) return design;

  const moved = [...rows];
  [moved[at], moved[target]] = [moved[target], moved[at]];
  const reordered = moved.flat();

  // Spliced back into the whole order, so the other pages keep their places.
  let next = 0;
  const pageSectionOrder = subSection.pageSectionOrder
    .map(id => (onPage.includes(id) ? reordered[next++] : id));

  return withSubSection(design, subSection.id, { ...subSection, pageSectionOrder });
}

/**
 * Moves a block's row to a new place on its page: in front of the row now at `position`, or to the
 * end where `position` is the row count. What a drag drops, where moveBlock is what one arrow key
 * does -- and the same rows as moveBlock, for the same reason: a reader sees a row move, so dragging
 * one half of a pair takes the pair with it rather than leaving two halves standing alone.
 *
 * A drop where the row already is changes nothing, and returns the same design.
 */
export function moveBlockToRow(design: ReportDesign, blockId: string, position: number): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block) return design;

  const page = blockPage(block);
  const onPage = subSection.pageSectionOrder
    .filter(id => subSection.pageSections[id] && blockPage(subSection.pageSections[id]) === page);
  const rows = layoutRows(subSection, page)
    .map(row => [row.full, row.left, row.right].filter(Boolean).map(entry => entry!.id));

  const at = rows.findIndex(row => row.includes(blockId));
  const to = Math.max(0, Math.min(rows.length, Math.trunc(position)));
  if (at === -1 || !Number.isFinite(to) || to === at || to === at + 1) return design;

  const moved = [...rows];
  const [row] = moved.splice(at, 1);
  moved.splice(to > at ? to - 1 : to, 0, row);
  const reordered = moved.flat();

  // Spliced back into the whole order, so the other pages keep their places.
  let next = 0;
  const pageSectionOrder = subSection.pageSectionOrder
    .map(id => (onPage.includes(id) ? reordered[next++] : id));

  return withSubSection(design, subSection.id, { ...subSection, pageSectionOrder });
}

/**
 * How wide a module is. Which side a half sits on is not chosen in the panel -- it is where the half
 * is put, by dragging it or with the arrow keys on its grip -- so this is all the panel asks. The
 * design still stores the side (`segmentLocation` is left or right), which is what lets a half sit
 * on the right with nothing beside it.
 */
export type BlockWidth = 'full' | 'half';

/** Where a dragged module is let go: in a gap between rows, or over one half of a row. */
export type BlockDrop =
  | { kind: 'gap'; position: number }
  | { kind: 'side'; row: number; side: 'left' | 'right' }
  | { kind: 'onto'; targetId: string };

type Half = 'left' | 'right';

const otherSide = (side: Half): Half => (side === 'left' ? 'right' : 'left');

/** One page's rows, as block ids. */
function pageRowIds(subSection: ReportSubSection, page: number): string[][] {
  return layoutRows(subSection, page)
    .map(row => [row.full, row.left, row.right].filter(Boolean).map(entry => entry!.id));
}

/** The same blocks grouped into the same rows, in the same order of rows. */
function sameRows(a: string[][], b: string[][]): boolean {
  const key = (rows: string[][]) => rows.map(row => [...row].sort().join(',')).join('|');
  return key(a) === key(b);
}

/** A page's order and the sides of some of its halves, to be tried. See `arrange`. */
interface Arrangement {
  order: string[];
  sides: Record<string, SegmentLocation>;
}

/**
 * The first arrangement that lays the page out as `expected`, or null if none does.
 *
 * Needed because rows are not stored. `layoutRows` pairs neighbouring halves of opposite sides as it
 * walks the order, so putting a half somewhere can pull it in beside a lone half next to it, or leave
 * the half it came from to join one -- a change to rows nobody touched. Each move here says what it
 * means to draw, tries the orders and sides that could draw it, and keeps only one that draws exactly
 * that. A move nothing draws as meant is not offered, rather than made and left to surprise.
 */
function arrange(
  design: ReportDesign,
  subSection: ReportSubSection,
  page: number,
  expected: string[][],
  tries: Arrangement[],
): ReportDesign | null {
  const onPage = subSection.pageSectionOrder
    .filter(id => subSection.pageSections[id] && blockPage(subSection.pageSections[id]) === page);

  for (const { order, sides } of tries) {
    // Spliced back into the whole order, so the other pages keep their places.
    let next = 0;
    const pageSectionOrder = subSection.pageSectionOrder
      .map(id => (onPage.includes(id) ? order[next++] : id));
    const pageSections = { ...subSection.pageSections };
    for (const [id, side] of Object.entries(sides)) {
      pageSections[id] = { ...pageSections[id], segmentLocation: side };
    }
    const candidate = { ...subSection, pageSectionOrder, pageSections };
    if (sameRows(pageRowIds(candidate, page), expected)) {
      return withSubSection(design, subSection.id, candidate);
    }
  }
  return null;
}

/**
 * A module made full or half width. Made half, it keeps a row of its own, on the left unless that
 * would pull it in beside a lone half next to it -- then on the right. Where both would, on the left
 * regardless: there is no side that keeps it alone, and the left is where a half starts.
 */
export function setBlockWidth(design: ReportDesign, blockId: string, width: BlockWidth): ReportDesign {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block) return design;
  if ((width === 'half') === (block.segmentLocation !== 'full')) return design;
  if (width === 'full') return withBlock(design, subSection, { ...block, segmentLocation: 'full' });

  const page = blockPage(block);
  const rows = pageRowIds(subSection, page);
  const order = rows.flat();
  return arrange(design, subSection, page, rows, [
    { order, sides: { [blockId]: 'left' } },
    { order, sides: { [blockId]: 'right' } },
  ]) ?? withBlock(design, subSection, { ...block, segmentLocation: 'left' });
}

/**
 * A dragged module let go, or null where letting go there would change nothing or would not draw as
 * the drag showed it would. The deck asks this on every move of the pointer, and only offers a drop
 * this answers.
 *
 * Dropped onto another card, any card swaps with it: each takes the other's place and size, so a
 * half dropped onto a full card becomes full there and the full one becomes the half it left. A
 * card that should keep its size is dropped above or below one instead, in a gap.
 *
 * A full-width module moves as its row, between rows -- or into the empty side of a half that is
 * alone, where it becomes half width on that side. A half moves by itself -- its partner stays, now
 * alone -- and can go three places: a gap between rows, where it gets a row of its own; the
 * empty side of a half that is alone; or the other side of its own row, which swaps it with its
 * partner, or moves it across where it has none. The arrow keys on a half's grip are the last of
 * these.
 */
export function dropBlock(design: ReportDesign, blockId: string, drop: BlockDrop): ReportDesign | null {
  const subSection = locateBlock(design, blockId);
  const block = subSection?.pageSections[blockId];
  if (!subSection || !block) return null;

  if (drop.kind === 'onto') return swapBlocks(design, subSection, blockId, drop.targetId);

  if (block.segmentLocation === 'full' && drop.kind === 'gap') {
    const moved = moveBlockToRow(design, blockId, drop.position);
    return moved === design ? null : moved;
  }

  const page = blockPage(block);
  const rows = pageRowIds(subSection, page);
  const at = rows.findIndex(row => row.includes(blockId));
  if (at === -1) return null;

  // The half it leaves behind, which is alone once this goes. Tried on its own side first; turned
  // over only if, left where it was, it would pair with a lone half beside it.
  const partnerId = rows[at].find(id => id !== blockId);
  const partner = partnerId ? subSection.pageSections[partnerId] : undefined;
  const partnerSides: Record<string, Half>[] = partner && partner.segmentLocation !== 'full'
    ? [{ [partner.id]: partner.segmentLocation }, { [partner.id]: otherSide(partner.segmentLocation) }]
    : [{}];
  const without = rows.map(row => row.filter(id => id !== blockId));

  if (block.segmentLocation === 'full') {
    // A full-width card has no other side of its own row to go to; it can only join a half.
    if (drop.kind === 'side' && drop.row === at) return null;
  } else {
    const side = block.segmentLocation;
    const moved = dropHalf(design, subSection, page, rows, without, at, blockId, side, partner, partnerSides, drop);
    if (moved !== undefined) return moved;
  }
  if (drop.kind !== 'side') return null;

  // Beside a half that is alone, in the side it leaves empty. A full-width card dropped there
  // becomes half width on that side -- dragging it in is how a full card is made half of a pair.
  const target = rows[drop.row];
  const host = target?.length === 1 ? subSection.pageSections[target[0]] : undefined;
  if (!host || host.segmentLocation === 'full' || host.segmentLocation === drop.side) return null;
  const expected = without
    .map((ids, index) => (index === drop.row ? [...ids, blockId] : ids))
    .filter(row => row.length);
  const orders = [[host.id, blockId], [blockId, host.id]].map(row =>
    without.flatMap((ids, index) => (index === drop.row ? row : ids)));
  return arrange(design, subSection, page, expected, partnerSides.flatMap(kept =>
    orders.map(order => ({ order, sides: { ...kept, [blockId]: drop.side } }))));
}

/**
 * Two cards on one page trading places, each taking the other's spot and size -- full, or the side
 * of the half it lands on. Null for anything nothing would draw as a clean swap (see arrange).
 */
function swapBlocks(
  design: ReportDesign,
  subSection: ReportSubSection,
  blockId: string,
  targetId: string,
): ReportDesign | null {
  const block = subSection.pageSections[blockId];
  const target = subSection.pageSections[targetId];
  if (!block || !target || blockId === targetId) return null;
  const page = blockPage(block);
  if (blockPage(target) !== page) return null;

  const rows = pageRowIds(subSection, page);
  const swapped = (id: string) => (id === blockId ? targetId : id === targetId ? blockId : id);
  const expected = rows.map(row => row.map(swapped));
  const order = rows.flat().map(swapped);
  const sides = { [blockId]: target.segmentLocation, [targetId]: block.segmentLocation };
  return arrange(design, subSection, page, expected, [{ order, sides }]);
}

/**
 * The drops only a half can make: into a gap, where it gets a row of its own, and across its own
 * row. Undefined for a drop beside another half, which a full-width card can make too and dropBlock
 * handles for both.
 */
function dropHalf(
  design: ReportDesign,
  subSection: ReportSubSection,
  page: number,
  rows: string[][],
  without: string[][],
  at: number,
  blockId: string,
  side: Half,
  partner: PageSection | undefined,
  partnerSides: Record<string, Half>[],
  drop: BlockDrop,
): ReportDesign | null | undefined {
  if (drop.kind === 'gap') {
    const position = Math.max(0, Math.min(rows.length, Math.trunc(drop.position)));
    if (!Number.isFinite(position)) return null;
    if (!partner && (position === at || position === at + 1)) return null;
    const expected = [...without.slice(0, position), [blockId], ...without.slice(position)]
      .filter(row => row.length);
    const order = expected.flat();
    return arrange(design, subSection, page, expected, partnerSides.flatMap(kept => [
      { order, sides: { ...kept, [blockId]: side } },
      { order, sides: { ...kept, [blockId]: otherSide(side) } },
    ]));
  }
  // A swap onto another card is dropBlock's, for halves and full cards alike.
  if (drop.kind !== 'side') return undefined;

  if (!rows[drop.row] || drop.side === side && drop.row === at) return null;

  if (drop.row === at) {
    // Its own row: across to the empty side, or swapped with its partner.
    const pair = partner ? [blockId, partner.id] : [blockId];
    const orders = [pair, [...pair].reverse()].map(row =>
      rows.flatMap((ids, index) => (index === at ? row : ids)));
    const sides = { [blockId]: drop.side, ...(partner ? { [partner.id]: side } : {}) };
    return arrange(design, subSection, page, rows, orders.map(order => ({ order, sides })));
  }
  return undefined;
}

/** Whether a subsection holds a stage matrix or an explainer, which a subsection has one pair of. */
function holdsStagePair(subSection: ReportSubSection): boolean {
  return Object.values(subSection.pageSections)
    .some(block => block?.reportType === 'stage-matrix' || block?.reportType === 'stage-explainer');
}

/**
 * Whether a block can be moved into another subsection.
 *
 * Any module can, with two exceptions that both come from a stage matrix and its explainer being one
 * thing on two pages. An explainer paired with a matrix does not move on its own -- the matrix moves,
 * and takes it along. And neither goes where a matrix or an explainer already is: the pair is found
 * by being the one in its subsection (see explainerFor), so a second would leave both explainers
 * reading whichever matrix came first.
 */
export function canMoveBlockTo(design: ReportDesign, blockId: string, targetSubSectionId: string): boolean {
  const source = locateBlock(design, blockId);
  const block = source?.pageSections[blockId];
  const target = findSubSection(design, targetSubSectionId);
  if (!source || !block || !target || source.id === target.id) return false;
  if (block.reportType !== 'stage-matrix' && block.reportType !== 'stage-explainer') return true;

  const pairedExplainer = block.reportType === 'stage-explainer'
    && Object.values(source.pageSections).some(other => other?.reportType === 'stage-matrix');
  return !pairedExplainer && !holdsStagePair(target);
}

/**
 * Moves a block into another subsection, at the end of its last page, at the width it had.
 *
 * The end of the last page rather than a page of its own, so nothing already laid out moves. A
 * stage matrix brings its explainer, and the two take pages of their own after the last -- they sit
 * on separate pages to begin with -- the way round they were, so the pair arrives as it left.
 *
 * A subsection holding only modules nobody has set up -- the blank one every new subsection starts
 * with -- has them replaced by what is moved in, rather than kept in front of it: they were never
 * anything, and a deck should not end up with a "choose a question" card above the module that
 * was dropped there. One set-up module anywhere in it and every module stays.
 *
 * Taking the last module off a page takes the page with it, as removing one does -- pages are read
 * off the blocks, not stored. Refused, by returning the same design, wherever canMoveBlockTo says no.
 */
export function moveBlockToSubSection(
  design: ReportDesign,
  blockId: string,
  targetSubSectionId: string,
): ReportDesign {
  if (!canMoveBlockTo(design, blockId, targetSubSectionId)) return design;
  const source = locateBlock(design, blockId)!;
  const block = source.pageSections[blockId];

  const explainer = block.reportType === 'stage-matrix' ? explainerFor(design, blockId) : undefined;
  const movingIds = source.pageSectionOrder.filter(id => id === blockId || id === explainer?.id);
  const moving = movingIds.map(id => source.pageSections[id]);

  const pageSections = { ...source.pageSections };
  movingIds.forEach(id => delete pageSections[id]);
  const without = withSubSection(design, source.id, {
    ...source,
    pageSections,
    pageSectionOrder: source.pageSectionOrder.filter(id => !movingIds.includes(id)),
  });

  const target = findSubSection(without, targetSubSectionId)!;
  const there = target.pageSectionOrder.map(id => target.pageSections[id]).filter(Boolean);
  const replaced = new Set(there.every(isUnsetModule) ? there.map(block => block.id) : []);
  const kept = there.filter(block => !replaced.has(block.id));
  const lastPage = kept.length ? Math.max(...kept.map(blockPage)) : 0;
  const start = moving.length > 1 && kept.length ? lastPage + 1 : lastPage;
  const first = Math.min(...moving.map(blockPage));

  const targetSections = { ...target.pageSections };
  replaced.forEach(id => delete targetSections[id]);
  return withSubSection(without, target.id, {
    ...target,
    pageSections: {
      ...targetSections,
      ...Object.fromEntries(moving.map((moved, index) =>
        [movingIds[index], { ...moved, page: start + blockPage(moved) - first }])),
    },
    pageSectionOrder: [...target.pageSectionOrder.filter(id => !replaced.has(id)), ...movingIds],
  });
}

/**
 * A module nobody has set up: one that charts questions, with none chosen and no title written --
 * what a new subsection, or Add module, starts with, drawn as "choose a question". A module that
 * charts nothing (headline stats, the action list) is never one: its words are its content.
 */
function isUnsetModule(block: PageSection): boolean {
  const keys = blockVariableKeys(block) ?? [];
  return !block.header?.trim() && keys.length > 0 && keys.every(key => !key);
}

/**
 * Takes a block out, the last one of a subsection included: an empty subsection is a page being
 * built, and the edit panel's Add module is right beside it. A subsection no one fills in shows its
 * heading and nothing under it.
 */
export function removeBlock(design: ReportDesign, blockId: string): ReportDesign {
  const subSection = locateBlock(design, blockId);
  if (!subSection) return design;

  const pageSections = { ...subSection.pageSections };
  delete pageSections[blockId];

  return withSubSection(design, subSection.id, {
    ...subSection,
    pageSections,
    pageSectionOrder: subSection.pageSectionOrder.filter(id => id !== blockId),
  });
}
